<template>
  <div>
    <v-data-table :headers="headers" :items="groups" style="width: 100%;" :search="searchGroups" :loading="loading">
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-btn small color="#212121" class="white--text" @click="createDialog = true">
            <v-icon class="align-center" color="primary">mdi-plus-thick</v-icon>
            <span class="pl-2">{{ $t('components.GroupManager.createGroup') }}</span>
          </v-btn>
          <v-dialog v-model="createDialog" hide-overlay persistent max-width="400">
            <v-card v-if="createDialog">
              <v-overlay :value="creatingGroup" absolute>
                <v-progress-circular
                  indeterminate
                  size="64"
                  color="primary"
                ></v-progress-circular>
                <div class="mt-2 text-center">{{ $t('components.GroupManager.creatingGroup') }}</div>
              </v-overlay>
              <v-toolbar dark color="primary">
                <v-btn icon dark @click="createDialog = false; newGroupName = ''; newGroupError = ''" :disabled="creatingGroup">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-toolbar-title>{{ $t('components.GroupManager.newGroup') }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                </v-toolbar-items>
              </v-toolbar>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="12" md="12">
                       <v-text-field v-model="newGroupName" :rules="rules.required" :label="$t('components.GroupManager.name')" required autofocus @input="newGroupError = ''" @keyup.enter="triggerCreateGroup" :disabled="creatingGroup"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row v-if="newGroupError.length > 0">
                    <v-col cols="12" sm="12" md="12">
                       <span style="color: red;">{{ newGroupError }}</span>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn :disabled="newGroupName === '' || creatingGroup" :loading="creatingGroup" color="primary" text @click="triggerCreateGroup">{{ $t('components.GroupManager.create') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="searchGroups"
            append-icon="mdi-magnify"
            :label="$t('components.GroupManager.search')"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
      </template>
      <template v-slot:item.users="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="selectedUsers = []; selectedGroup = item; dialogUsers = true">
              <v-icon small>mdi-account-supervisor</v-icon>
            </v-btn>
          </template>
          {{ $t('components.GroupManager.manageGroupUsers') }}
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="selectedModels = []; selectedGroup = item; dialogModels = true">
              <v-icon small>mdi-dots-hexagon</v-icon>
            </v-btn>
          </template>
          {{ $t('components.GroupManager.manageGroupModels') }}
        </v-tooltip>
        <v-tooltip top v-if="authUser.isManager">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="selectedReferences = []; selectedGroup = item; dialogReferences = true">
              <v-icon small>mdi-stamper</v-icon>
            </v-btn>
          </template>
          {{ $t('components.GroupManager.manageGroupReferences') }}
        </v-tooltip>
        <v-tooltip top v-if="authUser.isManager">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="selectedEkrms = []; selectedGroup = item; dialogEkrms = true">
              <v-icon small>mdi-head-snowflake</v-icon>
            </v-btn>
          </template>
          {{ $t('components.GroupManager.manageGroupEkrms') }}
        </v-tooltip>
        <v-tooltip top v-if="authUser.isManager">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
            <v-dialog v-model="deleteDialog" max-width="400" :retain-focus="false">
              <v-card v-if="deleteDialog">
                <v-card-title class="headline">{{ $t('components.GroupManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.GroupManager.deletingGroup') }} <b>{{ deleteItem.name }}</b>
                  <br/>
                  {{ $t('components.GroupManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="deleteDialog = false">{{ $t('components.GroupManager.cancel') }}</v-btn>
                  <v-btn color="red darken-1" text @click="triggerDeleteGroup(deleteItem.id); deleteDialog = false">{{ $t('components.GroupManager.delete') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          {{ $t('components.GroupManager.deleteGroup') }}
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog v-model="dialogUsers" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card v-if="dialogUsers">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="dialogUsers = false; selectedGroup = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.GroupManager.usersForGroup') }} {{ selectedGroup.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click.stop="saveDialog = true">{{ $t('components.GroupManager.save') }}</v-btn>
            <v-dialog v-model="saveDialog" max-width="400">
              <v-card v-if="saveDialog">
                <v-card-title class="headline">{{ $t('components.GroupManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.GroupManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions v-if="!processing">
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="saveDialog = false">{{ $t('components.GroupManager.abort') }}</v-btn>
                  <v-btn color="red darken-1" text @click="updateGroupUsers()">{{ $t('components.GroupManager.confirm') }}</v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  {{ $t('components.GroupManager.processing') }}
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <v-row>
          <v-col cols="12">
            <v-data-table v-model="selectedUsers" :headers="usersHeaders" :items="userRows" class="elevation-1" style="width: 95%;" show-select item-key="id" :items-per-page="50" :search="searchUsers" :loading="loading">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchUsers"
                    append-icon="mdi-magnify"
                    :label="$t('components.GroupManager.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogModels" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card v-if="dialogModels">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="dialogModels = false; selectedGroup = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.GroupManager.modelsForGroup') }} {{ selectedGroup.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click.stop="saveDialog = true">{{ $t('components.GroupManager.save') }}</v-btn>
            <v-dialog v-model="saveDialog" max-width="400">
              <v-card v-if="saveDialog">
                <v-card-title class="headline">{{ $t('components.GroupManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.GroupManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions v-if="!processing">
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="saveDialog = false">{{ $t('components.GroupManager.abort') }}</v-btn>
                  <v-btn color="red darken-1" text @click="updateGroupModels()">{{ $t('components.GroupManager.confirm') }}</v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  {{ $t('components.GroupManager.processing') }}
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <v-row>
          <v-col cols="12">
            <v-data-table v-model="selectedModels" :headers="modelsHeaders" :items="filteredModels" class="elevation-1" style="width: 95%;" show-select item-key="id" :items-per-page="50" :search="searchModels" :loading="loading">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-switch class="pt-5 pr-5 pl-5" v-model="showModelsMmm" :label="$t('components.GroupManager.adHoc')"></v-switch>
                  <v-switch class="pt-5" v-model="showModelsCustom" :label="$t('components.GroupManager.custom')"></v-switch>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchModels"
                    append-icon="mdi-magnify"
                    :label="$t('components.GroupManager.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template v-slot:item.date_created="{ item }">
                {{ item.date_created.toDate().toString().slice(4, 15) }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogReferences" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card v-if="dialogReferences">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="dialogReferences = false; selectedGroup = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.GroupManager.referencesForGroup') }} {{ selectedGroup.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click.stop="saveDialog = true">{{ $t('components.GroupManager.save') }}</v-btn>
            <v-dialog v-model="saveDialog" max-width="400">
              <v-card v-if="saveDialog">
                <v-card-title class="headline">{{ $t('components.GroupManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.GroupManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions v-if="!processing">
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="saveDialog = false">{{ $t('components.GroupManager.abort') }}</v-btn>
                  <v-btn color="red darken-1" text @click="updateGroupReferences()">{{ $t('components.GroupManager.confirm') }}</v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  {{ $t('components.GroupManager.processing') }}
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <v-row>
          <v-col cols="12">
            <v-data-table v-model="selectedReferences" :headers="referencesHeaders" :items="filteredReferences" class="elevation-1" style="width: 95%;" show-select item-key="id" :items-per-page="50" :search="searchReferences" :loading="loading">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchReferences"
                    append-icon="mdi-magnify"
                    :label="$t('components.GroupManager.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template v-slot:item.date_created="{ item }">
                {{ item.date_created.toDate().toString().slice(4, 15) }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialogEkrms" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card v-if="dialogEkrms">
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="dialogEkrms = false; selectedGroup = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.GroupManager.ekrmsForGroup') }} {{ selectedGroup.name }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click.stop="saveDialog = true">{{ $t('components.GroupManager.save') }}</v-btn>
            <v-dialog v-model="saveDialog" max-width="400">
              <v-card v-if="saveDialog">
                <v-card-title class="headline">{{ $t('components.GroupManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.GroupManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions v-if="!processing">
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="saveDialog = false">{{ $t('components.GroupManager.abort') }}</v-btn>
                  <v-btn color="red darken-1" text @click="updateGroupEkrms()">{{ $t('components.GroupManager.confirm') }}</v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  {{ $t('components.GroupManager.processing') }}
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <v-row>
          <v-col cols="12">
            <v-data-table v-model="selectedEkrms" :headers="ekrmsHeaders" :items="filteredEkrms" class="elevation-1" style="width: 95%;" show-select item-key="id" :items-per-page="50" :search="searchEkrms" :loading="loading">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchEkrms"
                    append-icon="mdi-magnify"
                    :label="$t('components.GroupManager.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template v-slot:item.date_created="{ item }">
                {{ item.date_created.toDate().toString().slice(4, 15) }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isComposerEkrm } from '@/utils/ekrmOrigin.js'
export default {
  data () {
    return {
      searchGroups: '',
      searchUsers: '',
      searchModels: '',
      searchReferences: '',
      searchEkrms: '',
      loading: true,
      processing: false,
      selectedGroup: {},
      dialogUsers: false,
      dialogModels: false,
      dialogReferences: false,
      dialogEkrms: false,
      saveDialog: false,
      createDialog: false,
      creatingGroup: false,
      newGroupName: '',
      newGroupError: '',
      deleteDialog: false,
      deleteItem: {},
      selectedUsers: [],
      selectedModels: [],
      selectedReferences: [],
      selectedEkrms: [],
      showModelsMmm: true,
      showModelsCustom: true,
      modelsLoaded: false,
      referencesLoaded: false,
      ekrmsLoaded: false
    }
  },
  methods: {
    ...mapActions('manager', [
      'createGroup',
      'deleteGroup',
      'loadGroups',
      'setGroupUsers',
      'loadCustomModels',
      'loadMmmModels',
      'setGroupModels',
      'setGroupReferences',
      'setGroupEkrms',
      'getGroupData',
      'loadReferenceModels',
      'loadEkrmModels',
      'cleanupGroupManagerListeners'
    ]),
    updateGroupModels: async function () {
      console.log('updateGroupModels')
      this.processing = true

      try {
        const result = await this.setGroupModels({
          groupId: this.selectedGroup.id,
          selectedModels: this.selectedModels.map(model => model.id)
        })

        if (result && result.success) {
          console.log('Successfully updated group models:', result.message)
        }

        // Close dialogs and reset state
        this.saveDialog = false
        this.dialogModels = false
        this.selectedGroup = {}
      } catch (error) {
        console.error('Error updating group models:', error)
        // Keep dialogs open so user can retry
      } finally {
        this.processing = false
      }
    },
    updateGroupReferences: async function () {
      console.log('updateGroupReferences')
      this.processing = true

      try {
        const result = await this.setGroupReferences({
          groupId: this.selectedGroup.id,
          selectedReferences: this.selectedReferences.map(reference => reference.id)
        })

        if (result && result.success) {
          console.log('Successfully updated group references:', result.message)
        }

        // Close dialogs and reset state
        this.saveDialog = false
        this.dialogReferences = false
        this.selectedGroup = {}
      } catch (error) {
        console.error('Error updating group references:', error)
        // Keep dialogs open so user can retry
      } finally {
        this.processing = false
      }
    },
    updateGroupEkrms: async function () {
      console.log('updateGroupEkrms')
      this.processing = true

      try {
        const result = await this.setGroupEkrms({
          groupId: this.selectedGroup.id,
          selectedEkrms: this.selectedEkrms.map(ekrm => ekrm.id)
        })

        if (result && result.success) {
          console.log('Successfully updated group EKRMs:', result.message)
        }

        // Close dialogs and reset state
        this.saveDialog = false
        this.dialogEkrms = false
        this.selectedGroup = {}
      } catch (error) {
        console.error('Error updating group EKRMs:', error)
        // Keep dialogs open so user can retry
      } finally {
        this.processing = false
      }
    },
    updateGroupUsers: async function () {
      console.log('updateGroupUsers')
      this.processing = true

      try {
        const result = await this.setGroupUsers({
          groupId: this.selectedGroup.id,
          selectedUsers: this.selectedUsers.map(user => user.id)
        })

        if (result && result.success) {
          console.log('Successfully updated group users:', result.message)
        }

        // Close dialogs and reset state
        this.saveDialog = false
        this.dialogUsers = false
        this.selectedGroup = {}
      } catch (error) {
        console.error('Error updating group users:', error)
        // Keep dialogs open so user can retry
      } finally {
        this.processing = false
      }
    },
    triggerCreateGroup () {
      if (this.groups.some(e => e.name === this.newGroupName)) {
        this.newGroupError = this.$t('components.GroupManager.nameAlreadyUsed')
      } else {
        this.creatingGroup = true
        this.createGroup({
          name: this.newGroupName,
          userAgent: navigator.userAgent
        }).then((result) => {
          this.newGroupName = ''
          this.createDialog = false
          console.log('Group created:', result.message)
          // Show success message if needed
          if (this.$emit) {
            this.$emit('show-alert', {
              type: 'success',
              message: result.message || 'Group created successfully'
            })
          }
        }).catch((error) => {
          console.error('Error creating group:', error)
          this.newGroupError = error.message || 'Failed to create group'
        }).finally(() => {
          this.creatingGroup = false
        })
      }
    },
    triggerDeleteGroup (groupId) {
      this.deleteGroup({
        id: groupId,
        userAgent: navigator.userAgent
      }).then((result) => {
        console.log('Group deleted:', result.message)
        // Show success message if needed
        if (this.$emit) {
          this.$emit('show-alert', {
            type: 'success',
            message: result.message || 'Group deleted successfully'
          })
        }
      }).catch((error) => {
        console.error('Error deleting group:', error)
        // Show error message if needed
        if (this.$emit) {
          this.$emit('show-alert', {
            type: 'error',
            message: error.message || 'Failed to delete group'
          })
        }
      })
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'managedUsers',
      'groups',
      'customModels',
      'mmmModels',
      'referenceModelsArray',
      'ekrmModels'
    ]),
    rules () {
      return {
        required: [v => !!v || this.$t('components.GroupManager.required')]
      }
    },
    headers () {
      return [
        { text: this.$t('components.GroupManager.name'), value: 'name', width: '80%' },
        { text: '', value: 'users' }
      ]
    },
    usersHeaders () {
      return [{ text: this.$t('components.GroupManager.userHeader'), value: 'name' }]
    },
    modelsHeaders () {
      return [
        { text: this.$t('components.GroupManager.modelHeader'), value: 'name' },
        { text: this.$t('components.GroupManager.typeHeader'), value: 'type' },
        { text: this.$t('components.GroupManager.createdHeader'), value: 'date_created' }
      ]
    },
    referencesHeaders () {
      return [
        { text: this.$t('components.GroupManager.referenceHeader'), value: 'config.category' },
        { text: this.$t('components.GroupManager.createdHeader'), value: 'date_created' }
      ]
    },
    ekrmsHeaders () {
      return [
        { text: this.$t('components.GroupManager.ekrmHeader'), value: 'definition.reference.category' },
        { text: this.$t('components.GroupManager.createdHeader'), value: 'date_created' }
      ]
    },
    // Lean, frozen row projections fed to the selection v-data-tables. Only the
    // displayed columns (+ id) are included so the selection arrays and search stay
    // cheap even with thousands of records (select-all no longer copies thousands of
    // full Firestore documents), and freezing keeps Vue from deep-observing them.
    userRows () {
      return this.managedUsers.map(u => Object.freeze({
        id: u.id,
        name: u.name || u.email
      }))
    },
    customModelRows () {
      return this.customModels.map(m => Object.freeze({
        id: m.id,
        name: m.name,
        type: m.type,
        date_created: m.date_created
      }))
    },
    mmmModelRows () {
      return this.mmmModels.map(m => Object.freeze({
        id: m.id,
        name: m.name,
        type: m.type,
        date_created: m.date_created
      }))
    },
    filteredModels () {
      var models = []
      if (this.showModelsCustom) {
        models = models.concat(this.customModelRows)
      }
      if (this.showModelsMmm) {
        models = models.concat(this.mmmModelRows)
      }
      models.sort(function (a, b) { return b.date_created - a.date_created })
      return models
    },
    filteredReferences () {
      var references = [].concat(this.referenceModelsArray)
      references.sort(function (a, b) { return b.date_created - a.date_created })
      return references
    },
    // The Model Composer's EKRMs are not assignable: their models carry their own permissions
    visibleEkrms () {
      return this.ekrmModels.filter(ekrm => !isComposerEkrm(ekrm))
    },
    filteredEkrms () {
      var ekrms = [].concat(this.visibleEkrms)
      ekrms.sort(function (a, b) { return b.date_created - a.date_created })
      return ekrms
    }
  },
  watch: {
    'selectedGroup': function (group) {
      if (Object.keys(group).length > 0) {
        this.loading = true
        this.getGroupData({ id: group.id }).then(groupData => {
          // Use Sets for O(1) membership instead of Array.includes() per item
          // (the old O(n*m) filter froze the tab on large groups). Selection is
          // matched to the table rows by item-key ('id'), so building it from the
          // lean row projections is enough.
          if (groupData.users) {
            const userSet = new Set(groupData.users)
            this.selectedUsers = this.userRows.filter(user => userSet.has(user.id))
          } else {
            this.selectedUsers = []
          }
          if (groupData.models) {
            const modelSet = new Set(groupData.models)
            this.selectedModels = this.customModelRows.concat(this.mmmModelRows).filter(model => modelSet.has(model.id))
          } else {
            this.selectedModels = []
          }
          if (groupData.references) {
            const referenceSet = new Set(groupData.references)
            this.selectedReferences = this.referenceModelsArray.filter(reference => referenceSet.has(reference.id))
          } else {
            this.selectedReferences = []
          }
          if (groupData.ekrms) {
            const ekrmSet = new Set(groupData.ekrms)
            this.selectedEkrms = this.visibleEkrms.filter(ekrm => ekrmSet.has(ekrm.id))
          } else {
            this.selectedEkrms = []
          }
          this.loading = false
        })
      }
    },
    // OPTIMIZATION: Lazy load models when dialog opens
    'dialogModels': async function (isOpen) {
      if (isOpen && !this.modelsLoaded) {
        this.loading = true
        await Promise.all([
          this.loadCustomModels(),
          this.loadMmmModels()
        ])
        this.modelsLoaded = true
        this.loading = false
      }
    },
    // OPTIMIZATION: Lazy load references when dialog opens
    'dialogReferences': async function (isOpen) {
      if (isOpen && !this.referencesLoaded && this.authUser.isManager) {
        this.loading = true
        await this.loadReferenceModels()
        this.referencesLoaded = true
        this.loading = false
      }
    },
    // OPTIMIZATION: Lazy load EKRMs when dialog opens
    'dialogEkrms': async function (isOpen) {
      if (isOpen && !this.ekrmsLoaded && this.authUser.isManager) {
        this.loading = true
        await this.loadEkrmModels()
        this.ekrmsLoaded = true
        this.loading = false
      }
    }
  },
  created: async function () {
    // OPTIMIZATION: Only load essential data upfront (groups only)
    // managedUsers are loaded by parent component (manageUserModels.vue)
    // Models, references, and EKRMs will be loaded on-demand when dialogs open
    await this.loadGroups()
    this.loading = false
  },
  beforeDestroy: function () {
    this.cleanupGroupManagerListeners()
  }
}
</script>
